/* eslint-env browser */
// Users › Driver onboarding (wave 3, W2_Portal_Settings look): three steps —
// the driver's details, their documents, their vehicle — shown as a step bar
// and one card at a time, one label above each field, errors under the field
// that is wrong, Skip and the step's one main button at the bottom.
//
// Writes, exactly as before and in the same order:
// 1. Details: checkUserExists, then addUser({ ...form, createdAt, referralId,
//    adminApprovedTrue, lang, fleetadmin for fleet admin / call centre /
//    scout }); the new driver's id is found by phone in the refreshed user
//    list (fetchUsersOnce, with the same waits and one retry).
// 2. Documents: updateLicenseImage(id, file, field) for each chosen file,
//    one after the other.
// 3. Vehicle: editCar({ ...car, driver, createdAt, car_image, fleetadmin },
//    'Add'), the picture (one indexed read of this driver's cars, then
//    editCar(car, 'UpdateImage')), then updateUserCar(id, { carType,
//    vehicleNumber, vehicleMake, vehicleModel, other_info, car_image,
//    carApproved, updateAt }); the car lists refetch once (markCarsStale).
// The phone is entered as 9 plain digits (+251 outside the box) and saved as
// +251…, as before.
import React, { useState, useRef, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { api, store, FirebaseContext } from 'common';
import { get } from 'firebase/database';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import PersonAddAltRoundedIcon from '@mui/icons-material/PersonAddAltRounded';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import { getLangKey } from 'common/src/other/getLangKey';
import { markCarsStale } from '../utils/carsSession';
import { tokens as c } from '../theme';
import { showToast, InlineMessage, EmptyState } from '../components/feedback';
import { BackButton, PageHeader, SectionCard, FieldLabel, FieldError, IconCircle, toNineDigits } from '../components/modern';
import { processApiResponse } from '../utils/securityUtils';

const EMAIL_RE =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

// Bank list
const bankList = [
  { id: 130, name: 'Abay Bank' },
  { id: 772, name: 'Addis International Bank' },
  { id: 207, name: 'Ahadu Bank' },
  { id: 656, name: 'Awash Bank' },
  { id: 347, name: 'Bank of Abyssinia' },
  { id: 571, name: 'Berhan Bank' },
  { id: 128, name: 'CBEBirr' },
  { id: 946, name: 'Commercial Bank of Ethiopia (CBE)' },
  { id: 893, name: 'Coopay-Ebirr' },
  { id: 880, name: 'Dashen Bank' },
  { id: 301, name: 'Global Bank Ethiopia' },
  { id: 534, name: 'Hibret Bank' },
  { id: 315, name: 'Lion International Bank' },
  { id: 266, name: 'M-Pesa' },
  { id: 979, name: 'Nib International Bank' },
  { id: 423, name: 'Oromia International Bank' },
  { id: 855, name: 'telebirr' },
  { id: 472, name: 'Wegagen Bank' },
  { id: 687, name: 'Zemen Bank' },
];

const PLACEHOLDER_CAR_IMAGE = 'https://cdn.pixabay.com/photo/2012/04/13/20/37/car-33556__480.png';

const emptyUser = () => ({
  firstName: '',
  lastName: '',
  mobile: '',
  email: '',
  bankName: '',
  bankAccount: '',
  tin: '',
  verifyId: '',
  usertype: 'driver',
  approved: true,
  walletBalance: 0,
  topUpBalance: 0,
  driverActiveStatus: false,
  queue: false,
});
const emptyDocs = () => ({ verifyIdImage: null, licenseImage: null, licenseImageBack: null, insuranceImage: null });

function UnifiedDriverOnboarding() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux selectors
  const auth = useSelector((state) => state.auth);
  const settings = useSelector((state) => state.settingsdata.settings);
  const staticusers = useSelector((state) => state.usersdata.staticusers);
  const cartypes = useSelector((state) => state.cartypes);
  const languagedata = useSelector((state) => state.languagedata);

  // API functions
  const { checkUserExists, addUser, updateLicenseImage, editCar, updateUserCar, fetchUsersOnce } = api;
  const { carsByDriverRef } = useContext(FirebaseContext);

  // State management
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [createdUserId, setCreatedUserId] = useState(null);
  const [multiLanguage, setMultiLanguage] = useState({});
  const [langSelection] = useState(1);

  // Step 1: User Information
  const [userData, setUserData] = useState(emptyUser);

  // Step 2: Document Upload
  const [documents, setDocuments] = useState(emptyDocs);
  const [documentPreviews, setDocumentPreviews] = useState(emptyDocs);

  // Step 3: Car Information
  const [carData, setCarData] = useState({
    vehicleNumber: '',
    vehicleMake: '',
    vehicleModel: '',
    carType: '',
    other_info: '',
    active: true,
    approved: !settings?.carApproval,
  });

  // Car image state
  const [carImage, setCarImage] = useState(null);
  const [carImagePreview, setCarImagePreview] = useState(null);
  const carImageRef = useRef();

  // File input refs
  const fileInputRefs = {
    verifyIdImage: useRef(),
    licenseImage: useRef(),
    licenseImageBack: useRef(),
    insuranceImage: useRef(),
  };

  const L = (key, dflt, opts) => t(key, { defaultValue: dflt, ...(opts || {}) });

  useEffect(() => {
    if (languagedata.langlist) {
      setMultiLanguage(languagedata.langlist);
    }
  }, [languagedata.langlist]);

  // No /cars fetch on this page at all: nothing here reads the cars slice, and
  // handleAssignCar finds the car it just created with an indexed read of that
  // one driver's cars (not the 5.8 MB whole-node read for staff roles).
  useEffect(() => {
    dispatch(fetchUsersOnce());
  }, [dispatch, fetchUsersOnce]);

  useEffect(() => {
    // Set agent/fleetadmin for call center agents
    if (auth.profile.usertype === 'callcenteragent' || auth.profile.usertype === 'callcentersupervisor') {
      const currentUser = staticusers?.find((user) => user.id === auth.profile.uid);
      if (currentUser) {
        setUserData((prev) => ({
          ...prev,
          fleetadmin: currentUser.id,
        }));
      }
    }
  }, [auth.profile, staticusers]);

  const normalizePhone = (input) => {
    if (!input) return null;
    const cleaned = input.trim().replace(/\D/g, '');
    if (/^251[1-9]\d{8}$/.test(cleaned)) return `+${cleaned}`;
    if (/^[1-9]\d{8}$/.test(cleaned)) return `+251${cleaned}`;
    if (/^0[1-9]\d{8}$/.test(cleaned)) return `+251${cleaned.slice(1)}`;
    return null;
  };

  const clearProblem = (name) => {
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
    if (formError) setFormError(null);
  };

  const handleUserInputChange = (e) => {
    const { id, value } = e.target;
    const updatedData = { ...userData, [id]: value };

    // Auto-generate email when names are entered
    if (id === 'firstName' || id === 'lastName') {
      const firstName = (id === 'firstName' ? value : userData.firstName || '').toLowerCase().replace(/[^a-z0-9]/g, '');
      const lastName = (id === 'lastName' ? value : userData.lastName || '').toLowerCase().replace(/[^a-z0-9]/g, '');

      const currentEmail = userData.email || '';
      if (!currentEmail || currentEmail.endsWith('@donkeydrive.io')) {
        if (firstName && lastName) {
          updatedData.email = `${firstName}.${lastName}@donkeydrive.io`;
        } else if (firstName && !lastName) {
          updatedData.email = `${firstName}@donkeydrive.io`;
        } else if (!firstName && lastName) {
          updatedData.email = `${lastName}@donkeydrive.io`;
        }
      }
    }
    setUserData(updatedData);
    clearProblem(id === 'firstName' || id === 'lastName' ? 'name' : id);
  };

  const handleCarInputChange = (e) => {
    setCarData({ ...carData, [e.target.id]: e.target.value });
    clearProblem(e.target.id);
  };

  const handleDocumentUpload = (field) => (e) => {
    const file = e.target.files[0];
    if (file) {
      setDocuments((prev) => ({ ...prev, [field]: file }));

      // Create preview
      const reader = new window.FileReader();
      reader.onloadend = () => {
        setDocumentPreviews((prev) => ({ ...prev, [field]: reader.result }));
      };
      reader.readAsDataURL(file);
      if (formError) setFormError(null);
    }
  };

  const handleCarImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCarImage(file);

      // Create preview
      const reader = new window.FileReader();
      reader.onloadend = () => {
        setCarImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Step 1: Create User
  const handleCreateUser = async () => {
    setFormError(null);
    setErrors({});
    // Auto-generate email if needed
    let finalUserData = { ...userData };
    if ((!finalUserData.email || finalUserData.email.trim() === '') && finalUserData.firstName && finalUserData.lastName) {
      const firstName = finalUserData.firstName.toLowerCase().replace(/[^a-z0-9]/g, '');
      const lastName = finalUserData.lastName.toLowerCase().replace(/[^a-z0-9]/g, '');
      if (firstName && lastName) {
        finalUserData.email = `${firstName}.${lastName}@donkeydrive.io`;
      }
    }

    // Validation
    if (!(finalUserData.lastName && finalUserData.firstName)) {
      setErrors({ name: t('proper_input_name') });
      return;
    }
    if (!EMAIL_RE.test(finalUserData.email)) {
      setErrors({ email: t('proper_email') });
      return;
    }
    if (!finalUserData.mobile || !normalizePhone(finalUserData.mobile)) {
      setErrors({ mobile: t('proper_mobile') });
      return;
    }
    if (!finalUserData.bankName) {
      setErrors({ bankName: t('proper_bank_name') });
      return;
    }
    if (!finalUserData.bankAccount) {
      setErrors({ bankAccount: t('proper_bank_account_number') });
      return;
    }

    setLoading(true);

    try {
      finalUserData.mobile = normalizePhone(finalUserData.mobile);

      // Check if user exists
      const res = await checkUserExists(finalUserData);
      const decryptedRes = await processApiResponse(res);
      if (decryptedRes.users && decryptedRes.users.length > 0) {
        setFormError(t('user_exists'));
        setLoading(false);
        return;
      }

      // Generate referral ID
      const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
      const reference = [...Array(5)].map(() => chars[~~(Math.random() * chars.length)]).join('');

      // Prepare user data
      finalUserData.createdAt = new Date().getTime();
      finalUserData.referralId = reference;
      finalUserData.adminApprovedTrue = finalUserData.approved;

      // Set language
      let arr = Object.keys(languagedata.langlist || {});
      if (arr.length > 0) {
        finalUserData.lang = {
          langLocale: multiLanguage[arr[langSelection]]?.langLocale || 'en',
          dateLocale: multiLanguage[arr[langSelection]]?.dateLocale || 'en',
        };
      }

      // Set agent if applicable
      if (auth.profile.usertype === 'fleetadmin') {
        finalUserData.fleetadmin = auth.profile.uid;
      } else if (auth.profile.usertype === 'callcenteragent' || auth.profile.usertype === 'callcentersupervisor' || auth.profile.usertype === 'scout') {
        finalUserData.fleetadmin = auth.profile.uid;
      }

      // Store the user data for later reference
      const userIdentifier = {
        email: finalUserData.email,
        mobile: finalUserData.mobile,
      };

      // Add user
      await dispatch(addUser(finalUserData));

      // Wait for Firebase to update
      await new Promise((resolve) => setTimeout(resolve, 2000));

      // Fetch fresh user data
      await dispatch(fetchUsersOnce());

      // Wait for state to update
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Get users from store, find the created user
      const allUsers = store.getState().usersdata?.staticusers || [];
      const createdUser = allUsers.find((user) => user.mobile === userIdentifier.mobile);

      if (createdUser) {
        setCreatedUserId(createdUser.id);
        showToast.success(t('driver_basic_info_saved'));
        setActiveStep(1);
        setLoading(false);
      } else {
        // Try once more
        await new Promise((resolve) => setTimeout(resolve, 2000));
        await dispatch(fetchUsersOnce());
        await new Promise((resolve) => setTimeout(resolve, 1000));

        const retryUsers = store.getState().usersdata?.staticusers || [];
        const retryUser = retryUsers.find((user) => user.mobile === userIdentifier.mobile);

        if (retryUser) {
          setCreatedUserId(retryUser.id);
          showToast.success(t('driver_basic_info_saved'));
          setActiveStep(1);
        } else {
          console.error('Could not find created user after retries');
          // Still move to next step but with a warning
          showToast.success(t('driver_basic_info_saved'));
          setActiveStep(1);
        }
        setLoading(false);
      }
    } catch (error) {
      console.error('Error creating user:', error);
      setFormError(t('error_creating_user'));
      setLoading(false);
    }
  };

  // Step 2: Upload Documents
  const handleUploadDocuments = async () => {
    setFormError(null);
    if (!createdUserId) {
      setFormError(t('user_not_created_yet'));
      return;
    }

    setLoading(true);
    let uploadCount = 0;

    try {
      // Upload each document
      for (const [field, file] of Object.entries(documents)) {
        if (file) {
          await dispatch(updateLicenseImage(createdUserId, file, field));
          uploadCount++;
        }
      }

      if (uploadCount === 0) {
        setFormError(t('no_doc_uploaded'));
        setLoading(false);
        return;
      }

      // Wait for uploads to complete
      setTimeout(() => {
        showToast.success(t('documents_uploaded_successfully'));
        setActiveStep(2);
        setLoading(false);
      }, 2000);
    } catch (error) {
      console.error('Error uploading documents:', error);
      setFormError(t('error_uploading_documents'));
      setLoading(false);
    }
  };

  // Step 3: Assign Car
  const handleAssignCar = async () => {
    setFormError(null);
    setErrors({});
    if (!createdUserId) {
      setFormError(t('user_not_created_yet'));
      return;
    }

    // Validation
    if (!carData.vehicleNumber) {
      setErrors({ vehicleNumber: t('car_no_not_found') });
      return;
    }
    if (!carData.vehicleMake) {
      setErrors({ vehicleMake: t('vehicleMake_required') });
      return;
    }
    if (!carData.vehicleModel) {
      setErrors({ vehicleModel: t('vehicleModel_required') });
      return;
    }
    if (!carData.carType) {
      setErrors({ carType: t('carType_required') });
      return;
    }

    setLoading(true);

    try {
      // Prepare car data with default image
      const finalCarData = {
        ...carData,
        driver: createdUserId,
        createdAt: new Date().getTime(),
        car_image: carImage || PLACEHOLDER_CAR_IMAGE,
        fleetadmin: userData.fleetadmin || null,
      };

      // Add car first
      await dispatch(editCar(finalCarData, 'Add'));
      // EDIT_CAR is a no-op reducer: the new car (and its uploaded image) is not
      // in the Redux cars slice, so make the next cars list view refetch once.
      // Marked here so it still happens if the image upload below throws.
      markCarsStale();

      // If there's a custom car image, upload it
      if (carImage) {
        // Wait a bit for the car to be created
        await new Promise((resolve) => setTimeout(resolve, 2000));

        // Find the newly created car: one indexed read of just this driver's
        // cars (/cars is indexed on `driver`, ~1 KB) instead of the whole node.
        const snap = await get(carsByDriverRef(createdUserId));
        const driverCars = snap.val() || {};
        const createdCarId = Object.keys(driverCars).find((key) => driverCars[key].vehicleNumber === carData.vehicleNumber);
        const createdCar = createdCarId ? { ...driverCars[createdCarId], id: createdCarId } : null;

        if (createdCar) {
          const carWithImage = {
            ...createdCar,
            car_image: carImage,
          };
          await dispatch(editCar(carWithImage, 'UpdateImage'));
          // Again once the image has landed (a list opened during the upload
          // would otherwise count as fresh with the placeholder image).
          markCarsStale();
          // The UpdateImage thunk writes the download URL back onto the object it
          // was given; carImage itself is the File and must never reach /users.
          if (typeof carWithImage.car_image === 'string') finalCarData.car_image = carWithImage.car_image;
        }
      }

      // Update user with car info
      const updateData = {
        carType: carData.carType,
        vehicleNumber: carData.vehicleNumber,
        vehicleMake: carData.vehicleMake,
        vehicleModel: carData.vehicleModel,
        other_info: carData.other_info,
        // Never write the File object: if the upload did not produce a URL, fall
        // back to the same placeholder the car was created with.
        car_image: typeof finalCarData.car_image === 'string' ? finalCarData.car_image : PLACEHOLDER_CAR_IMAGE,
        carApproved: !settings?.carApproval,
        updateAt: new Date().getTime(),
      };

      await dispatch(updateUserCar(createdUserId, updateData));

      showToast.success(t('driver_onboarding_complete'));

      // Reset form after 2 seconds for new entry
      setTimeout(() => {
        handleReset();
      }, 2000);
    } catch (error) {
      console.error('Error assigning car:', error);
      setFormError(t('error_assigning_car'));
    } finally {
      setLoading(false);
    }
  };

  // Skip document upload step
  const handleSkipDocuments = () => {
    setFormError(null);
    setActiveStep(2);
    showToast.info(t('documents_skipped_can_upload_later'));
  };

  // Skip car assignment step
  const handleSkipCar = () => {
    setFormError(null);
    showToast.success(t('driver_created_without_car'));

    // Reset form after 2 seconds for new entry
    setTimeout(() => {
      handleReset();
    }, 2000);
  };

  // Reset form for new entry
  const handleReset = () => {
    setActiveStep(0);
    setCreatedUserId(null);
    setUserData(emptyUser());
    setDocuments(emptyDocs());
    setDocumentPreviews(emptyDocs());
    setCarData({
      vehicleNumber: '',
      vehicleMake: '',
      vehicleModel: '',
      carType: '',
      other_info: '',
      active: true,
      approved: !settings?.carApproval,
    });
    setCarImage(null);
    setCarImagePreview(null);
    setErrors({});
    setFormError(null);
    dispatch(fetchUsersOnce());
  };

  const steps = [
    { label: t('driver_information'), icon: <PersonAddAltRoundedIcon /> },
    { label: t('upload_documents'), icon: <UploadFileOutlinedIcon /> },
    { label: t('assign_vehicle'), icon: <DirectionsCarOutlinedIcon /> },
  ];

  // ── Pieces ───────────────────────────────────────────────────────────
  // Short fields add columns as the screen grows (4 on very wide screens: the
  // details step is a long run of short fields); notes and the photo span the row.
  const grid = {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' },
    columnGap: 2,
    rowGap: 2.25,
    mt: 2,
  };
  const describedBy = (name) => (errors[name] ? `onboard-${name}-error` : undefined);
  const field = ({ id, label, value, onChange, error, inputProps, InputProps, multiline, autoComplete, type }) => (
    <Box sx={{ minWidth: 0, ...(multiline ? { gridColumn: '1 / -1' } : {}) }}>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <TextField
        id={id}
        fullWidth
        type={type}
        multiline={!!multiline}
        minRows={multiline ? 2 : undefined}
        value={value}
        disabled={loading}
        error={!!errors[error || id]}
        onChange={onChange}
        inputProps={{ autoComplete: autoComplete || 'off', 'aria-describedby': describedBy(error || id), ...(inputProps || {}) }}
        InputProps={InputProps}
      />
      <FieldError id={`onboard-${error || id}-error`}>{errors[error || id]}</FieldError>
    </Box>
  );
  const uploadTile = ({ key, label, preview, onClick, icon, emptyText }) => (
    <Box key={key} sx={{ minWidth: 0 }}>
      <FieldLabel>{label}</FieldLabel>
      <ButtonBase
        onClick={onClick}
        disabled={loading}
        aria-label={`${emptyText}: ${label}`}
        sx={{
          width: '100%',
          aspectRatio: '4 / 3',
          borderRadius: '12px',
          overflow: 'hidden',
          border: `2px dashed ${preview ? c.ACCENT : c.OUTLINE_STRONG}`,
          bgcolor: preview ? c.WHITE : c.SURFACE_ALT,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          color: c.INK_2,
          '&:hover': { borderColor: c.ACCENT },
          '&:focus-visible': { outline: `2px solid ${c.ACCENT}`, outlineOffset: 2 },
        }}
      >
        {preview ? (
          <Box component="img" src={preview} alt={label} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <>
            {icon}
            <Typography sx={{ fontSize: 14, fontWeight: 600, px: 1, textAlign: 'center' }}>{emptyText}</Typography>
          </>
        )}
      </ButtonBase>
    </Box>
  );
  const busyIcon = (icon) => (loading ? <CircularProgress size={16} color="inherit" /> : icon);
  const footer = (buttons) => (
    <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {formError ? <InlineMessage kind="error">{formError}</InlineMessage> : null}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1.5 }}>{buttons}</Box>
    </Box>
  );

  const docFields = [
    { field: 'verifyIdImage', label: t('verifyid_image') },
    { field: 'licenseImage', label: t('license_image_front') },
    { field: 'licenseImageBack', label: t('license_image_back') },
    { field: 'insuranceImage', label: t('insurance_image') },
  ];

  return (
    <Box dir={i18n.dir()} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <BackButton onClick={() => navigate('/users/1')} />
      <PageHeader title={t('unified_driver_onboarding')} />

      {/* Progress */}
      <Paper component="ol" aria-label={t('unified_driver_onboarding')} sx={{ listStyle: 'none', m: 0, p: { xs: 1.5, md: 2 }, display: 'flex', flexWrap: 'wrap', gap: { xs: 1.5, md: 3 } }}>
        {steps.map((step, index) => {
          const done = activeStep > index;
          const current = activeStep === index;
          return (
            <Box
              component="li"
              key={step.label}
              aria-current={current ? 'step' : undefined}
              sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flex: { xs: '1 1 100%', sm: '1 1 0' }, minWidth: 0 }}
            >
              <IconCircle icon={done ? <CheckRoundedIcon /> : step.icon} hue={done ? 'success' : current ? 'accent' : 'neutral'} size={36} />
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 12.5, color: c.TEXT_3, fontWeight: 600 }}>{`${index + 1} / ${steps.length}`}</Typography>
                <Typography sx={{ fontSize: 15, fontWeight: current ? 700 : 600, color: current ? c.INK : c.INK_2, overflowWrap: 'anywhere' }}>{step.label}</Typography>
              </Box>
            </Box>
          );
        })}
      </Paper>

      {/* Step 1: User Information */}
      {activeStep === 0 ? (
        <SectionCard icon={<PersonAddAltRoundedIcon />} hue="accent" title={t('driver_information')} id="onboard-details">
          <Box
            component="form"
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              if (!loading) handleCreateUser();
            }}
          >
            <Box sx={grid}>
              {field({ id: 'firstName', label: t('first_name'), value: userData.firstName, onChange: handleUserInputChange, error: 'name', autoComplete: 'given-name' })}
              {field({ id: 'lastName', label: t('last_name'), value: userData.lastName, onChange: handleUserInputChange, error: 'nameLast', autoComplete: 'family-name' })}
              {field({
                id: 'mobile',
                label: t('mobile'),
                type: 'tel',
                value: userData.mobile,
                onChange: (e) => {
                  setUserData({ ...userData, mobile: toNineDigits(e.target.value) });
                  clearProblem('mobile');
                },
                inputProps: { inputMode: 'numeric', maxLength: 9, dir: 'ltr' },
                InputProps: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Box component="span" sx={{ color: c.INK_2, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                        +251
                      </Box>
                    </InputAdornment>
                  ),
                },
              })}
              {field({ id: 'email', label: t('email'), type: 'email', value: userData.email, onChange: handleUserInputChange, inputProps: { inputMode: 'email', spellCheck: false } })}
              <Box sx={{ minWidth: 0 }}>
                <FieldLabel id="onboard-bank-label">{t('select_bank')}</FieldLabel>
                <TextField
                  select
                  fullWidth
                  value={userData.bankName || ''}
                  disabled={loading}
                  error={!!errors.bankName}
                  onChange={(e) => {
                    setUserData({ ...userData, bankName: e.target.value });
                    clearProblem('bankName');
                  }}
                  SelectProps={{ labelId: 'onboard-bank-label', SelectDisplayProps: { 'aria-describedby': describedBy('bankName') } }}
                >
                  {bankList.map((bank) => (
                    <MenuItem key={bank.id} value={bank.name}>
                      {bank.name}
                    </MenuItem>
                  ))}
                </TextField>
                <FieldError id="onboard-bankName-error">{errors.bankName}</FieldError>
              </Box>
              {field({ id: 'bankAccount', label: t('bankAccount'), value: userData.bankAccount, onChange: handleUserInputChange, inputProps: { inputMode: 'numeric', dir: 'ltr' } })}
              {field({ id: 'tin', label: t('tin_number'), value: userData.tin, onChange: handleUserInputChange, inputProps: { dir: 'ltr' } })}
              {field({ id: 'verifyId', label: t('id_or_passport'), value: userData.verifyId, onChange: handleUserInputChange })}
            </Box>
            {footer(
              <Button type="submit" variant="contained" color="success" disabled={loading} startIcon={busyIcon(<CheckRoundedIcon />)} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                {loading ? L('form_saving', 'Saving…') : t('save_and_continue')}
              </Button>
            )}
          </Box>
        </SectionCard>
      ) : null}

      {/* Step 2: Document Upload */}
      {activeStep === 1 ? (
        <SectionCard icon={<UploadFileOutlinedIcon />} hue="info" title={t('upload_documents')} id="onboard-docs">
          <Box sx={{ ...grid, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' } }}>
            {docFields.map((d) =>
              uploadTile({
                key: d.field,
                label: d.label,
                preview: documentPreviews[d.field],
                onClick: () => fileInputRefs[d.field].current && fileInputRefs[d.field].current.click(),
                icon: <FileUploadOutlinedIcon sx={{ fontSize: 36, color: c.ACCENT }} />,
                emptyText: t('click_to_upload'),
              })
            )}
          </Box>
          {docFields.map((d) => (
            <input key={d.field} ref={fileInputRefs[d.field]} type="file" hidden accept="image/*" onChange={handleDocumentUpload(d.field)} />
          ))}
          {footer(
            <>
              <Button variant="outlined" color="neutral" onClick={handleSkipDocuments} disabled={loading}>
                {t('skip')}
              </Button>
              <Button variant="contained" onClick={handleUploadDocuments} disabled={loading} startIcon={busyIcon(<FileUploadOutlinedIcon />)}>
                {loading ? L('form_saving', 'Saving…') : t('upload_and_continue')}
              </Button>
            </>
          )}
        </SectionCard>
      ) : null}

      {/* Step 3: Car Assignment */}
      {activeStep === 2 ? (
        <SectionCard icon={<DirectionsCarOutlinedIcon />} hue="success" title={t('assign_vehicle')} id="onboard-car">
          <Box sx={grid}>
            {field({ id: 'vehicleNumber', label: t('vehicle_reg_no'), value: carData.vehicleNumber, onChange: handleCarInputChange })}
            {field({ id: 'vehicleMake', label: t('vehicle_model_name'), value: carData.vehicleMake, onChange: handleCarInputChange })}
            {field({ id: 'vehicleModel', label: t('vehicle_model_no'), value: carData.vehicleModel, onChange: handleCarInputChange })}
            <Box sx={{ minWidth: 0 }}>
              <FieldLabel id="onboard-cartype-label">{t('car_type')}</FieldLabel>
              <TextField
                select
                fullWidth
                value={carData.carType || ''}
                disabled={loading}
                error={!!errors.carType}
                onChange={(e) => {
                  setCarData({ ...carData, carType: e.target.value });
                  clearProblem('carType');
                }}
                SelectProps={{ labelId: 'onboard-cartype-label', SelectDisplayProps: { 'aria-describedby': describedBy('carType') } }}
              >
                {(cartypes?.cars || []).map((carType) => (
                  <MenuItem key={carType.id} value={carType.name}>
                    {t(getLangKey(carType.name))}
                  </MenuItem>
                ))}
              </TextField>
              <FieldError id="onboard-carType-error">{errors.carType}</FieldError>
            </Box>
            {field({ id: 'other_info', label: t('other_info'), value: carData.other_info, onChange: handleCarInputChange, multiline: true })}
            <Box sx={{ gridColumn: '1 / -1', maxWidth: { sm: 420 } }}>
              {uploadTile({
                key: 'car',
                label: t('car_image'),
                preview: carImagePreview,
                onClick: () => carImageRef.current && carImageRef.current.click(),
                icon: <DirectionsCarOutlinedIcon sx={{ fontSize: 40, color: c.ACCENT }} />,
                emptyText: t('click_to_upload_car_image'),
              })}
              <input ref={carImageRef} type="file" hidden accept="image/*" onChange={handleCarImageUpload} />
            </Box>
          </Box>
          {footer(
            <>
              <Button variant="outlined" color="neutral" onClick={handleSkipCar} disabled={loading}>
                {t('skip')}
              </Button>
              <Button variant="contained" color="success" onClick={handleAssignCar} disabled={loading} startIcon={busyIcon(<TaskAltRoundedIcon />)}>
                {loading ? L('form_saving', 'Saving…') : t('complete_onboarding')}
              </Button>
            </>
          )}
        </SectionCard>
      ) : null}

      {/* Success message for completed onboarding */}
      {activeStep === steps.length ? (
        <Paper sx={{ p: 2 }}>
          <EmptyState icon={<TaskAltRoundedIcon />} title={t('driver_onboarding_complete')} actionLabel={t('add_another_driver')} onAction={handleReset} />
        </Paper>
      ) : null}
    </Box>
  );
}

export default UnifiedDriverOnboarding;
